export const EVALUATION_TYPES = {
  MANAGEMENT_REVIEW: "management_review",
  INTERNAL_AUDIT: "internal_audit",
};

export const EVALUATION_STATUSES = [
  "draft",
  "planned",
  "in_progress",
  "completed",
  "closed",
];

export const EVALUATION_RESULTS = ["passed", "failed", "conditional"];

export const TILTAK_ACTION_TYPES = ["generic", "new_routine", "linked_document"];

export const TILTAK_NEW_PATH = "/dashboard/hse-ks/tiltak/new";
export const TILTAK_VIEW_PATH = "/dashboard/hse-ks/tiltak";

const TYPE_ALIASES = {
  management_review: EVALUATION_TYPES.MANAGEMENT_REVIEW,
  managementreview: EVALUATION_TYPES.MANAGEMENT_REVIEW,
  review: EVALUATION_TYPES.MANAGEMENT_REVIEW,
  ledelses_gjennomgang: EVALUATION_TYPES.MANAGEMENT_REVIEW,
  ledelsens_gjennomgang: EVALUATION_TYPES.MANAGEMENT_REVIEW,
  internal_audit: EVALUATION_TYPES.INTERNAL_AUDIT,
  internalaudit: EVALUATION_TYPES.INTERNAL_AUDIT,
  audit: EVALUATION_TYPES.INTERNAL_AUDIT,
  internrevisjon: EVALUATION_TYPES.INTERNAL_AUDIT,
};

const STATUS_ALIASES = {
  draft: "draft",
  planned: "planned",
  scheduled: "planned",
  in_progress: "in_progress",
  inprogress: "in_progress",
  ongoing: "in_progress",
  started: "in_progress",
  completed: "completed",
  done: "completed",
  closed: "closed",
  cancelled: "closed",
  canceled: "closed",
};

export function normalizeEvaluationType(value, fallback = "") {
  if (!value) return fallback;
  const key = String(value).trim().toLowerCase().replace(/[\s-]+/g, "_");
  return TYPE_ALIASES[key] || fallback || key;
}

export function normalizeEvaluationStatus(value, fallback = "draft") {
  if (!value) return fallback;
  const key = String(value).trim().toLowerCase().replace(/[\s-]+/g, "_");
  return STATUS_ALIASES[key] || fallback;
}

export function firstPresent(record, keys, fallback = "") {
  if (!record) return fallback;
  for (const key of keys) {
    const value = record[key];
    if (value !== undefined && value !== null && value !== "") return value;
  }
  return fallback;
}

export function toDateInput(value) {
  if (!value) return "";
  const raw = String(value);
  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) return raw.slice(0, 10);
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";
  return parsed.toISOString().slice(0, 10);
}

export function formatDisplayDate(value) {
  if (!value) return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return parsed.toLocaleDateString("nb-NO");
}

export function personLabel(person) {
  if (!person) return "";
  if (typeof person === "string") return person;
  return (
    person.displayTitle ||
    person.display_title ||
    person.full_name ||
    person.name ||
    [person.first_name, person.last_name].filter(Boolean).join(" ") ||
    person.email ||
    ""
  );
}

export function findingsTextOf(raw) {
  const value = raw?.findings;
  if (value == null) return "";
  if (typeof value === "string") return value;
  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (typeof item === "string") return item;
        return [item?.title, item?.description].filter(Boolean).join(" — ");
      })
      .filter(Boolean)
      .join("\n");
  }
  return String(value);
}

export function normalizeEvaluation(raw, forcedType = "") {
  if (!raw || typeof raw !== "object") return null;
  const kind = normalizeEvaluationType(
    firstPresent(raw, ["kind", "evaluation_type", "type", "review_type", "audit_type"], forcedType),
    forcedType
  );

  return {
    id: raw.id,
    kind,
    evaluation_type: kind,
    title: firstPresent(raw, ["title", "name", "subject"], ""),
    status: normalizeEvaluationStatus(raw.status),
    result: raw.result || "",
    scheduled_on: toDateInput(raw.scheduled_on),
    held_on: toDateInput(firstPresent(raw, ["held_on", "conducted_on"], "")),
    conducted_on: toDateInput(firstPresent(raw, ["held_on", "conducted_on"], "")),
    period_from: toDateInput(raw.period_from),
    period_to: toDateInput(raw.period_to),
    next_review_on: toDateInput(raw.next_review_on),
    scope: firstPresent(raw, ["scope"], ""),
    summary: firstPresent(raw, ["summary"], ""),
    findings: findingsTextOf(raw),
    conclusions: firstPresent(raw, ["conclusions", "conclusion"], ""),
    conclusion: firstPresent(raw, ["conclusions", "conclusion"], ""),
    responsible_user_id: raw.responsible_user_id || raw.lead_user_id || raw.responsible_user?.id || "",
    lead_user_id: raw.responsible_user_id || raw.lead_user_id || raw.responsible_user?.id || "",
    lead: raw.responsible_user || raw.lead || raw.created_by || null,
    responsible_user: raw.responsible_user || null,
    open_tiltak_count: raw.open_tiltak_count || 0,
    created_at: raw.created_at,
    updated_at: raw.updated_at,
    raw,
  };
}

export function unwrapEvaluationList(payload) {
  if (Array.isArray(payload)) return { items: payload, meta: {} };
  const items = payload?.data || [];
  return {
    items: Array.isArray(items) ? items : [],
    meta: payload?.meta || {},
  };
}

export function unwrapEvaluationRecord(payload) {
  if (!payload || typeof payload !== "object") return payload;
  return payload.data || payload.evaluation || payload;
}

export function blankToNull(value) {
  return value === "" || value === undefined ? null : value;
}

export function toApiEvaluationPayload(form, kind) {
  return {
    title: form.title?.trim() || "",
    kind,
    status: form.status || "draft",
    scheduled_on: blankToNull(form.scheduled_on),
    held_on: blankToNull(form.held_on || form.conducted_on),
    period_from: blankToNull(form.period_from),
    period_to: blankToNull(form.period_to),
    next_review_on: blankToNull(form.next_review_on),
    result: blankToNull(form.result),
    scope: form.scope || "",
    summary: form.summary || "",
    findings: typeof form.findings === "string" ? form.findings : findingsTextOf({ findings: form.findings }),
    conclusions: form.conclusions || form.conclusion || "",
    responsible_user_id: blankToNull(form.responsible_user_id || form.lead_user_id),
  };
}

export function evaluationPath(evaluation, suffix = "") {
  const type = normalizeEvaluationType(evaluation?.kind || evaluation?.evaluation_type);
  const base =
    type === EVALUATION_TYPES.INTERNAL_AUDIT
      ? "/dashboard/hse-ks/evaluation/audits"
      : "/dashboard/hse-ks/evaluation/management-review";
  if (!evaluation?.id) return suffix ? `${base}/${suffix}` : base;
  return suffix ? `${base}/${evaluation.id}/${suffix}` : `${base}/${evaluation.id}`;
}

export function tiltakCreatePath({ evaluationId, title } = {}) {
  const params = new URLSearchParams();
  if (evaluationId) params.set("hse_evaluation_id", String(evaluationId));
  if (title) params.set("title", title);
  params.set("source", "hse_evaluation");
  const query = params.toString();
  return query ? `${TILTAK_NEW_PATH}?${query}` : TILTAK_NEW_PATH;
}

export function tiltakViewPath(actionId) {
  return actionId ? `${TILTAK_VIEW_PATH}/${actionId}` : TILTAK_VIEW_PATH;
}

export function statusTone(status) {
  switch (normalizeEvaluationStatus(status)) {
    case "planned":
      return "bg-blue-50 text-blue-800 ring-blue-200";
    case "in_progress":
      return "bg-amber-50 text-amber-800 ring-amber-200";
    case "completed":
      return "bg-emerald-50 text-emerald-800 ring-emerald-200";
    case "closed":
      return "bg-slate-200 text-slate-700 ring-slate-300";
    default:
      return "bg-slate-100 text-slate-700 ring-slate-200";
  }
}

export function resultTone(result) {
  switch (String(result || "").toLowerCase()) {
    case "passed":
      return "bg-emerald-50 text-emerald-800 ring-emerald-200";
    case "failed":
      return "bg-rose-50 text-rose-800 ring-rose-200";
    case "conditional":
      return "bg-amber-50 text-amber-800 ring-amber-200";
    default:
      return "bg-slate-100 text-slate-700 ring-slate-200";
  }
}
